import { useState } from "react"

import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts"

import type { BarChartElement } from "./catalog"

import {
  CHART_OUTLINE,
  ChartTitle,
  colorForSeries,
  DEFAULT_YAXIS_WIDTH,
  formatTickNumber,
  sharedTooltipProps,
} from "./chart-shared"
import { type DrillContext, DrillThroughSheet } from "../components/drill-through-sheet"
import { TileEmptyState } from "../components/tile-empty-state"
import { useChartClickFilter } from "../hooks/use-chart-click-filter"
import { pickThresholdColor } from "../utils/thresholds"

export function BarChartComponent({ element }: { element: BarChartElement }) {
  // Single-series bar charts don't benefit from a legend — the title
  // already names the series. Hiding it removes a redundant row of
  // chrome and lets the bars breathe.
  const showLegend = element.bars.length > 1
  const isVertical = element.layout === "vertical"
  // Click-to-filter: writes the clicked bar's xKey value to state.
  // When configured, individual <Bar> components get an onClick;
  // when omitted, no event handler is attached so the cursor doesn't
  // suggest interactivity that isn't there.
  const handleBarClickFilter = useChartClickFilter(element.onClickFilter, element.xKey)
  // Drill-through descriptor is mutually compatible with filter
  // click — admins can wire either or both. When both are set, the
  // filter fires AND the sheet opens, in that order (the filter
  // mutates state synchronously before we mount the sheet, so the
  // drill query inherits the freshly-filtered scope).
  const [drillContext, setDrillContext] = useState<DrillContext | null>(null)
  const drill = element.drillThrough
  const handleBarClick = (data: unknown) => {
    if (handleBarClickFilter) handleBarClickFilter(data)
    if (!drill) return
    const obj = data as { payload?: Record<string, unknown> } | Record<string, unknown> | null
    const row =
      obj && typeof obj === "object" && "payload" in obj
        ? (obj.payload as Record<string, unknown>)
        : (obj as Record<string, unknown> | null)
    if (!row) return
    setDrillContext({
      drill,
      row: row as Record<string, string | number | null>,
    })
  }
  const cursorIsInteractive = !!handleBarClickFilter || !!drill

  // Bail to a clean "Inga data" placeholder when the data array is
  // missing/empty. recharts otherwise renders just the axes, which
  // reads as "broken chart" rather than "no rows yet".
  if (!Array.isArray(element.data) || element.data.length === 0) {
    return <TileEmptyState title={element.title} />
  }

  return (
    <div className="bg-card border-border rounded-xl border p-4 shadow-sm">
      <ChartTitle title={element.title} />
      <ResponsiveContainer height={280} width="100%">
        <BarChart
          data={element.data}
          layout={isVertical ? "vertical" : "horizontal"}
          margin={{ top: 8, right: 8, bottom: 0, left: 0 }}
        >
          {/* Per-series vertical gradient: solid at the bar tip,
              fading ~30% at the base. Gives the bars a soft "lit
              from above" feel without being gauche. */}
          <defs>
            {element.bars.map((bar, i) => {
              const c = colorForSeries(bar.label ?? bar.dataKey, i, bar.color)
              return (
                <linearGradient
                  key={bar.dataKey}
                  id={`bar-gradient-${i}`}
                  x1="0"
                  x2="0"
                  y1="0"
                  y2="1"
                >
                  <stop offset="0%" stopColor={c} stopOpacity={1} />
                  <stop offset="100%" stopColor={c} stopOpacity={0.55} />
                </linearGradient>
              )
            })}
          </defs>
          <CartesianGrid
            className="stroke-border"
            horizontal={!isVertical}
            strokeDasharray="3 3"
            vertical={isVertical}
          />
          {isVertical ? (
            <>
              <YAxis
                className="text-xs"
                dataKey={element.xKey}
                tickLine={false}
                type="category"
                width={DEFAULT_YAXIS_WIDTH}
              />
              <XAxis
                axisLine={false}
                className="text-xs"
                tickFormatter={formatTickNumber}
                tickLine={false}
                type="number"
              />
            </>
          ) : (
            <>
              <XAxis axisLine={false} className="text-xs" dataKey={element.xKey} tickLine={false} />
              <YAxis
                axisLine={false}
                className="text-xs"
                tickFormatter={formatTickNumber}
                tickLine={false}
                width={DEFAULT_YAXIS_WIDTH}
              />
            </>
          )}
          <Tooltip cursor={{ fill: "hsl(var(--muted) / 0.4)" }} {...sharedTooltipProps} />
          {showLegend && <Legend iconType="circle" wrapperStyle={{ paddingTop: 8 }} />}
          {element.bars.map((bar, i) => {
            // Threshold colouring overrides the gradient fill on a
            // per-data-row basis. Recharts' Cell inside a Bar lets us
            // give each data point its own fill while keeping a single
            // <Bar> series — no need to fan out the data into multiple
            // dataKeys. Bars whose value doesn't match any threshold
            // (or whose dataKey is non-numeric) fall back to the
            // gradient.
            const hasThresholds = Array.isArray(bar.thresholds) && bar.thresholds.length > 0
            const data = Array.isArray(element.data) ? element.data : []
            return (
              <Bar
                key={bar.dataKey}
                cursor={cursorIsInteractive ? "pointer" : undefined}
                dataKey={bar.dataKey}
                fill={`url(#bar-gradient-${i})`}
                maxBarSize={56}
                name={bar.label ?? bar.dataKey}
                radius={isVertical ? [0, 6, 6, 0] : [6, 6, 0, 0]}
                stackId={bar.stackId}
                stroke={CHART_OUTLINE}
                strokeWidth={1}
                onClick={cursorIsInteractive ? handleBarClick : undefined}
              >
                {hasThresholds &&
                  data.map((row, rowIdx) => {
                    const cellColor = pickThresholdColor(
                      (row as Record<string, unknown>)[bar.dataKey],
                      bar.thresholds
                    )
                    return (
                      <Cell
                        key={`${bar.dataKey}-${rowIdx}`}
                        fill={cellColor ?? `url(#bar-gradient-${i})`}
                      />
                    )
                  })}
              </Bar>
            )
          })}
        </BarChart>
      </ResponsiveContainer>
      <DrillThroughSheet context={drillContext} onClose={() => setDrillContext(null)} />
    </div>
  )
}

// eslint-disable-next-line import/no-default-export
export default BarChartComponent
